import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DataTable from '../components/common/Table';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import FilterBar from '../components/common/FilterBar';
import { toast } from '../components/common/Toast';
import PromoteModal from '../components/PromoteModal';
import { usePermission } from '../hooks/usePermission';
import { useAuth } from '../hooks/useAuth';
import * as usersApi from '../api/users';
import * as apartmentsApi from '../api/apartments';
import { Apartment, Role, User } from '../types';
import { formatPhoneInput } from '../utils/phone';

type RoleFilter = Role | 'all';

const ROLE_LABEL: Record<Role, string> = {
  super_admin: '최상위 관리자',
  admin: '관리자',
  manager: '매니저',
  user: '운전자',
};

const ROLE_BADGE: Record<Role, string> = {
  super_admin: 'badge--red',
  admin: 'badge--green',
  manager: 'badge--orange',
  user: 'badge--gray',
};

const errMsg = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
};

const MembersPage: React.FC = () => {
  const { isSuperAdmin, isAdmin, isManager, canAccess } = usePermission();
  const { me, logout } = useAuth();
  const currentUserId = me?.id ?? null;

  // ISS-032 — 역할별 탭 가시성 (super_admin: 전체, admin: super_admin 숨김, manager: super_admin/admin 숨김)
  const availableRoleTabs: RoleFilter[] = isSuperAdmin
    ? ['all', 'super_admin', 'admin', 'manager', 'user']
    : isAdmin
      ? ['all', 'admin', 'manager', 'user']
      : isManager
        ? ['all', 'manager', 'user']
        : ['all'];

  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    phone: '', password: '', name: '', nickname: '',
    car_number: '', company: '', user_type: 'car',
  });

  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<User | null>(null);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedApts, setSelectedApts] = useState<string[]>([]);

  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState<User | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await usersApi.getList({
        page,
        limit: 20,
        role: roleFilter,
        include_inactive: includeInactive,
      });
      setUsers(res.items);
      setTotal(res.total);
    } catch (err) {
      toast.error(errMsg(err, '회원 목록을 불러올 수 없습니다'));
    }
  }, [page, roleFilter, includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  // ISS-032 — 현재 역할에서 접근 금지된 탭이면 'all' 로 리셋
  useEffect(() => {
    if (!availableRoleTabs.includes(roleFilter)) {
      setRoleFilter('all');
    }
    // availableRoleTabs 는 파생값이라 의도적으로 deps 에서 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, isAdmin, isManager]);

  const filtered = search
    ? users.filter(
        (u) =>
          u.phone.includes(search) ||
          u.nickname.includes(search) ||
          (u.name || '').includes(search) ||
          (u.car_number || '').includes(search)
      )
    : users;

  // ---- create ----
  const openCreate = () => {
    setCreateForm({ phone: '', password: '', name: '', nickname: '', car_number: '', company: '', user_type: 'car' });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.phone || !createForm.password || !createForm.name) {
      toast.error('전화번호, 비밀번호, 이름은 필수입니다');
      return;
    }
    try {
      await usersApi.createUser({
        phone: createForm.phone,
        password: createForm.password,
        name: createForm.name,
        nickname: createForm.nickname || undefined,
        car_number: createForm.car_number || undefined,
        company: createForm.company || undefined,
        user_type: createForm.user_type,
      });
      toast.success('회원이 생성되었습니다');
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, '회원 생성에 실패했습니다'));
    }
  };

  // ---- password reset ----
  const openPw = (u: User) => {
    setPwTarget(u);
    setNewPassword('');
    setPwOpen(true);
  };

  const handlePwReset = async () => {
    if (!pwTarget || !newPassword) return;
    try {
      await usersApi.resetUserPassword(pwTarget.id, newPassword);
      toast.success('비밀번호가 변경되었습니다');
      setPwOpen(false);
    } catch (err) {
      toast.error(errMsg(err, '비밀번호 변경에 실패했습니다'));
    }
  };

  // ---- deactivate / reactivate / hard delete ----
  const [selfDeactivateOpen, setSelfDeactivateOpen] = useState(false);
  const [selfDeactivatePassword, setSelfDeactivatePassword] = useState('');

  const handleDeactivate = async (u: User) => {
    if (u.id === currentUserId) {
      setSelfDeactivateOpen(true);
      return;
    }
    if (!window.confirm(`"${u.nickname}" 계정을 비활성화하시겠습니까?`)) return;
    try {
      await usersApi.deactivateUser(u.id);
      toast.success('비활성화되었습니다');
      load();
    } catch (err) {
      toast.error(errMsg(err, '비활성화에 실패했습니다'));
    }
  };

  const handleSelfDeactivate = async () => {
    if (!selfDeactivatePassword) {
      toast.error('비밀번호를 입력해주세요');
      return;
    }
    try {
      await usersApi.deactivateMe(selfDeactivatePassword);
      setSelfDeactivateOpen(false);
      setSelfDeactivatePassword('');
      logout();
      window.location.href = '/login';
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const code = (err.response?.data as { error_code?: string })?.error_code;
        if (code === 'WRONG_PASSWORD') {
          toast.error('비밀번호가 일치하지 않습니다');
          return;
        }
      }
      toast.error(errMsg(err, '계정 비활성화에 실패했습니다'));
    }
  };

  const handleReactivate = async (u: User) => {
    if (!window.confirm(`"${u.nickname}" 계정을 재활성화하시겠습니까?`)) return;
    try {
      await usersApi.reactivateUser(u.id);
      toast.success('재활성화되었습니다');
      load();
    } catch (err) {
      toast.error(errMsg(err, '재활성화에 실패했습니다'));
    }
  };

  const handleHardDelete = async (u: User) => {
    if (!window.confirm(`"${u.nickname}" 계정을 영구 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;
    try {
      await usersApi.hardDeleteUser(u.id);
      toast.success('영구 삭제되었습니다');
      load();
    } catch (err) {
      toast.error(errMsg(err, '영구 삭제에 실패했습니다'));
    }
  };

  // ---- assign apartments ----
  const openAssign = async (u: User) => {
    setAssignTarget(u);
    try {
      const [aptRes, assignedRes] = await Promise.all([
        apartmentsApi.getList({ page: 1, limit: 100 }),
        usersApi.getUserApartments(u.id),
      ]);
      setApartments(aptRes.items);
      setSelectedApts(assignedRes.apartment_ids);
      setAssignOpen(true);
    } catch (err) {
      toast.error(errMsg(err, '장소 할당 정보를 불러올 수 없습니다'));
    }
  };

  const toggleApt = (id: string) => {
    setSelectedApts((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const handleAssignSave = async () => {
    if (!assignTarget) return;
    try {
      await usersApi.setUserApartments(assignTarget.id, selectedApts);
      toast.success('담당 장소가 저장되었습니다');
      setAssignOpen(false);
      setAssignTarget(null);
    } catch (err) {
      toast.error(errMsg(err, '담당 장소 저장에 실패했습니다'));
    }
  };

  // ---- promote / demote ----
  const openPromote = (u: User) => {
    setPromoteTarget(u);
    setPromoteOpen(true);
  };

  const submitPromote = async (newRole: Role, apartmentIds: string[]) => {
    if (!promoteTarget) return;
    try {
      await usersApi.promoteUser(promoteTarget.id, newRole, apartmentIds);
      toast.success(`${ROLE_LABEL[newRole]}(으)로 승격되었습니다`);
      load();
    } catch (err) {
      toast.error(errMsg(err, '승격에 실패했습니다'));
      throw err;
    }
  };

  const handleDemote = async (u: User) => {
    if (!window.confirm(`"${u.nickname}"을(를) 운전자(user)로 강등하시겠습니까?\n담당 장소 배정이 모두 해제됩니다.`)) return;
    try {
      await usersApi.demoteUser(u.id);
      toast.success('강등되었습니다');
      load();
    } catch (err) {
      toast.error(errMsg(err, '강등에 실패했습니다'));
    }
  };

  // ---- columns ----
  const canManage = canAccess('members_manage');
  const canHardDelete = canAccess('hard_delete');

  const renderActions = (u: User) => {
    if (!canManage) return null;
    const protectedAccount = u.is_default_super_admin;
    // admin 은 super_admin / admin 대상 조작 불가 (백엔드가 최종 차단)
    const adminCantTouch = isAdmin && (u.role === 'super_admin' || u.role === 'admin');
    // ISS-066 — default super_admin 도 다른 super_admin 은 비활성화/재활성화/비번리셋 가능
    // (ISS-056 정책: deactivate_user/reactivate/reset_password_by_admin 백엔드 가드는 이미 허용).
    // 자기 자신 차단은 백엔드 deactivate_user 의 isSelf 체크로 보장 (router.py:328) — 프론트는 UI 만 정합.
    const isSelf = currentUserId !== null && u.id === currentUserId;
    const canActOnDefault = !protectedAccount || (isSuperAdmin && !isSelf);

    // 비활성 계정: [재활성화] + [영구 삭제] 두 버튼만 노출 (승격/비번리셋/담당장소/비활성화 숨김)
    if (!u.is_active) {
      return (
        <div className="table-actions">
          {!adminCantTouch && canActOnDefault && (
            <button className="btn btn--sm btn--primary" onClick={(e) => { e.stopPropagation(); handleReactivate(u); }}>
              재활성화
            </button>
          )}
          {canHardDelete && !protectedAccount && (
            <button className="btn btn--sm btn--danger" onClick={(e) => { e.stopPropagation(); handleHardDelete(u); }}>
              영구 삭제
            </button>
          )}
        </div>
      );
    }

    // 활성 계정: 기존 액션 전체
    return (
      <div className="table-actions">
        {!protectedAccount && !adminCantTouch && u.role === 'user' && (
          <button className="btn btn--sm btn--primary" onClick={(e) => { e.stopPropagation(); openPromote(u); }}>
            승격
          </button>
        )}
        {!protectedAccount && !adminCantTouch && u.role !== 'user' && u.role !== 'super_admin' && (
          <button className="btn btn--sm btn--secondary" onClick={(e) => { e.stopPropagation(); handleDemote(u); }}>
            강등
          </button>
        )}
        {!adminCantTouch && (u.role === 'admin' || u.role === 'manager') && (
          <button className="btn btn--sm btn--secondary" onClick={(e) => { e.stopPropagation(); openAssign(u); }}>
            담당 장소
          </button>
        )}
        {canActOnDefault && !adminCantTouch && (
          <button className="btn btn--sm btn--secondary" onClick={(e) => { e.stopPropagation(); openPw(u); }}>
            비번 리셋
          </button>
        )}
        {canActOnDefault && !adminCantTouch && (
          <button className="btn btn--sm btn--warning" onClick={(e) => { e.stopPropagation(); handleDeactivate(u); }}>
            비활성화
          </button>
        )}
        {!protectedAccount && canHardDelete && (
          <button className="btn btn--sm btn--danger" onClick={(e) => { e.stopPropagation(); handleHardDelete(u); }}>
            영구 삭제
          </button>
        )}
      </div>
    );
  };

  const isPending = (u: User) =>
    u.company_id !== null && u.company_is_approved === false;

  const pendingStyle = (u: User) =>
    isPending(u) ? ({ color: '#999' } as React.CSSProperties) : undefined;

  const pendingTitle = (u: User) => (isPending(u) ? '승인 대기중' : undefined);

  const columns = [
    { key: 'nickname', label: '닉네임', render: (u: User) => (
      <span style={pendingStyle(u)} title={pendingTitle(u)}>
        {u.nickname}
        {u.is_default_super_admin && <span title="기본 super_admin (보호)" style={{ marginLeft: 6 }}>🛡️</span>}
        {isPending(u) && <span style={{ marginLeft: 6, fontSize: 11 }}>(승인 대기중)</span>}
      </span>
    )},
    { key: 'name', label: '이름', render: (u: User) => (
      <span style={pendingStyle(u)} title={pendingTitle(u)}>{u.name || '-'}</span>
    )},
    { key: 'phone', label: '전화번호' },
    { key: 'role', label: '역할', render: (u: User) => (
      <span className={`badge ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</span>
    )},
    { key: 'company', label: '소속', render: (u: User) => {
      const name = u.company_name || u.company || '-';
      if (isPending(u)) {
        return <span style={{ color: '#999' }} title="승인 대기중">{name} (대기중)</span>;
      }
      return name;
    }},
    { key: 'last_login_at', label: '마지막 로그인', render: (u: User) =>
      u.last_login_at ? new Date(u.last_login_at).toLocaleString('ko-KR') : '-'
    },
    { key: 'is_active', label: '상태', render: (u: User) =>
      u.is_active
        ? <span className="badge badge--green">활성</span>
        : <span className="badge badge--gray">비활성</span>
    },
    { key: 'actions', label: '', render: renderActions },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>회원 관리</h2>
        {canManage && (
          <button className="btn btn--primary" onClick={openCreate}>회원 추가</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {availableRoleTabs.map((r) => (
          <button
            key={r}
            className={`btn btn--sm ${roleFilter === r ? 'btn--primary' : 'btn--secondary'}`}
            onClick={() => { setRoleFilter(r); setPage(1); }}
          >
            {r === 'all' ? '전체' : ROLE_LABEL[r as Role]}
          </button>
        ))}
        <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => { setIncludeInactive(e.target.checked); setPage(1); }}
          />
          비활성 포함
        </label>
      </div>

      <FilterBar onSearch={setSearch} searchPlaceholder="닉네임/이름/전화번호/차량번호 검색..." />
      <DataTable columns={columns} data={filtered} />
      <Pagination page={page} total={total} limit={20} onPageChange={setPage} />

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="회원 추가" onConfirm={handleCreate}>
        <div className="form-group"><label>전화번호 *</label>
          <input type="tel" maxLength={13} value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: formatPhoneInput(e.target.value) })} placeholder="010-0000-0000" /></div>
        <div className="form-group"><label>비밀번호 *</label>
          <input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="8자 이상, 대/소문자/숫자/특수문자" /></div>
        <div className="form-group"><label>이름 *</label>
          <input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} /></div>
        <div className="form-group"><label>닉네임 (선택)</label>
          <input value={createForm.nickname} onChange={(e) => setCreateForm({ ...createForm, nickname: e.target.value })} placeholder="비우면 전화번호로 설정" /></div>
        <div className="form-group"><label>차량번호</label>
          <input value={createForm.car_number} onChange={(e) => setCreateForm({ ...createForm, car_number: e.target.value })} /></div>
        <div className="form-group"><label>소속 회사</label>
          <input value={createForm.company} onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })} /></div>
        <div className="form-group"><label>유형</label>
          <select value={createForm.user_type} onChange={(e) => setCreateForm({ ...createForm, user_type: e.target.value })}>
            <option value="car">차량</option>
            <option value="motorcycle">오토바이</option>
            <option value="bicycle">자전거</option>
          </select></div>
        <p style={{ fontSize: 12, color: '#666' }}>
          모든 신규 회원은 운전자(user)로 생성됩니다. 승격은 목록에서 [승격] 버튼으로 진행하세요.
        </p>
      </Modal>

      <Modal isOpen={pwOpen} onClose={() => setPwOpen(false)} title={`비밀번호 리셋 — ${pwTarget?.nickname || ''}`} onConfirm={handlePwReset}>
        <div className="form-group"><label>새 비밀번호</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="8자 이상, 대/소문자/숫자/특수문자" /></div>
      </Modal>

      <Modal
        isOpen={assignOpen}
        onClose={() => { setAssignOpen(false); setAssignTarget(null); }}
        title={assignTarget ? `${assignTarget.nickname} — 담당 장소` : '담당 장소'}
        onConfirm={handleAssignSave}
        confirmText="저장"
      >
        <p style={{ marginBottom: 12, color: '#555' }}>
          admin/manager는 체크한 장소의 이벤트·통계만 조회·관리할 수 있습니다.
        </p>
        {apartments.length === 0 ? (
          <p>등록된 장소가 없습니다.</p>
        ) : (
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', padding: 12, borderRadius: 4 }}>
            {apartments.map((apt) => (
              <label key={apt.id} style={{ display: 'flex', alignItems: 'center', padding: '6px 0', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={selectedApts.includes(apt.id)}
                  onChange={() => toggleApt(apt.id)}
                />
                <span>{apt.name}</span>
                <span style={{ color: '#999', fontSize: 12 }}>{apt.address}</span>
              </label>
            ))}
          </div>
        )}
      </Modal>

      <PromoteModal
        isOpen={promoteOpen}
        onClose={() => { setPromoteOpen(false); setPromoteTarget(null); }}
        target={promoteTarget}
        onSubmit={submitPromote}
      />

      <Modal
        isOpen={selfDeactivateOpen}
        onClose={() => { setSelfDeactivateOpen(false); setSelfDeactivatePassword(''); }}
        title="본인 계정 비활성화"
        onConfirm={handleSelfDeactivate}
        confirmText="비활성화"
      >
        <p style={{ fontSize: 14, color: '#e53e3e', marginBottom: 16 }}>
          본인 계정을 비활성화하면 다시 로그인할 수 없습니다.
          관리자에게 문의해야 재활성화할 수 있습니다.
        </p>
        <div className="form-group">
          <label>비밀번호 확인</label>
          <input
            type="password"
            value={selfDeactivatePassword}
            onChange={(e) => setSelfDeactivatePassword(e.target.value)}
            placeholder="현재 비밀번호를 입력하세요"
          />
        </div>
      </Modal>

      {!isSuperAdmin && !isAdmin && !isManager && <p>회원 관리 권한이 없습니다.</p>}
    </div>
  );
};

export default MembersPage;
